import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { get, has, isArray, isEmpty, isEqual } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {
  OrgAdminOrganizationService
} from '../../../../ispm-admin/app/org-admin-settings/organizations/services/org-admin-organization.service';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';

@Component({
  selector: 'ispm-practitioner-handler',
  templateUrl: './practitioner-handler.component.html',
  styleUrls: ['./practitioner-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('PractitionerHandler')
export class PractitionerHandlerComponent implements OnDestroy, IWidgetItem {
  set widgetDisabledStatus(value) {
    this._widgetDisabledStatus = value;
    this._changeDetector.markForCheck();
  }

  get widgetDisabledStatus() {
    return this._widgetDisabledStatus;
  }

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    this.config = widgetConfig.widgetPayload.itemConfig;
    this.fromId = widgetConfig.widgetPayload.formId;
    this.isNewPractitioner = !widgetConfig.widgetPayload.editMode;
    this.enableEditButton = get(widgetConfig.widgetPayload, 'enableEditButton', false);
    this.controls = get(widgetConfig.widgetPayload, 'controls', []);
  }

  set widgetData(widgetData: any) {
    // Case for creating new practtioner - data will be {}
    if( widgetData && Object.keys(widgetData).length === 0 ){
      this.loading = false;
      this._changeDetector.markForCheck();
    }
    if (!widgetData || Object.keys(widgetData).length === 0 || isEqual(this.widgetDataDetails, widgetData)){
      this.showEditButton = this.isNewPractitioner && this.enableEditButton;
      return;
    }
    this.widgetDataDetails = widgetData;
    if (widgetData.credentialUser === true || widgetData.credentialUser === 'true') {
      widgetData.credentialUser = 'credentialUser';
      widgetData.practitionerType = 'Credential';
    } else if (widgetData.ssoUser === true || widgetData.ssoUser === 'true') {
      widgetData.credentialUser = 'ssoUser';
      widgetData.practitionerType = 'SSO';
    } else {
      widgetData.credentialUser = 'generalUser';
      widgetData.practitionerType = 'General';
    }

    if (has(widgetData, 'assignedServers') && widgetData.assignedServers.length > 0) {
      widgetData.assignedServer = {'id': widgetData.assignedServers[0].reference};
    }

    //Telecom items are displayed according to their order and not to their type.
    //The order is: 1. phone: mobile. 2. phone.home. 3. email.
    //So, if email will be the first item but the order is that phone: mobile is the first item that is displayed, the email will be displayed inside phone field.
    //To overcome this telcom array is sorted here.
    const telecom = [{system: 'phone', use: 'mobile', value: '', rank: 1, period: {start: '', end: ''}},
      {system: 'phone', use: 'home', value: '', rank: 1, period: {start: '', end: ''}},
      {system: 'email', use: '', value: '', rank: 1, period: {start: '', end: ''}}];

    const phones = widgetData.telecom.filter(item => item.system === 'phone');
    if (phones && phones.length > 0) {
      const mobile = phones.find(phone => phone.use === 'mobile');
      const home = phones.find(phone => phone.use === 'home');
      if (mobile) {
        telecom[0] = mobile;
      } else if (phones[0].use === '') { //If no phone.use === 'mobile', the first phone item will be mobile phone and the second (if exists) will be home phone
        telecom[0] = phones[0];
      }

      if (home) {
        telecom[1] = home;
      } else if (phones.length > 1 && phones[1].use === '') {
        telecom[1] = phones[1];
      }
    }

    const email = widgetData.telecom.find(item => item.system === 'email');
    if (email) {
      telecom[2] = email;
    }
    widgetData.telecom = telecom;

    this.data = widgetData;
    this.listenToDataChange = true;

    if (!this.data.practitionerRole[0] || !widgetData.practitionerRole[0].role.display) {
      return;
    }

    const role = {'name': ''};
    role.name = widgetData.practitionerRole[0].role.display;
    this.data.practitionerRole[0].role = role;
    this.originalData = JSON.parse(JSON.stringify(this.data));
    setTimeout(() => {
      this.showEditButton = this.enableEditButton;
      this._changeDetector.markForCheck();
    }, 900);
    this.loading = false;
    this._changeDetector.markForCheck();
  }

  get widgetFormInstanceSubmitted() {
    return this._widgetFormInstanceSubmitted;
  }

  set widgetFormInstanceSubmitted(widgetFormInstanceSubmitted: boolean) {
    //Due to some strange bug, dynamic forms does not send validateChangeEvent when managingOrganization is changed (although it is a required field).
    //To overcome this (in a non-elegnat way) adding a specific validation for managingOrganization.
    let managingOrganizationExist = false;
    if (this.isValid) {
      if (this.data && Array.isArray(this.data.practitionerRole) && this.data.practitionerRole.length > 0 && this.data.practitionerRole[0].hasOwnProperty('managingOrganization')) {
        managingOrganizationExist = !!this.data.practitionerRole[0].managingOrganization;
      }
    }

    if (!this.isValid || !managingOrganizationExist) {
      this._notificationMessageService.error('Please provide all the required inputs!');
      this.widgetSubmitStatusValueCallback(false);
      return;
    }

    this.data = this.replaceNull(this.data);
    const role = {'code': '', 'display': ''};
    role.code = this.data.practitionerRole[0].role.name;
    role.display = this.data.practitionerRole[0].role.name;
    this.data.practitionerRole[0].role = role;

    if (this.isValid && this.isFormChange && this.isNewPractitioner) {
      const practitioner = {};
      practitioner['name'] = this.data.name;
      practitioner['active'] = this.data.active;
      practitioner['communication'] = this.data.communication;

      if (this.data.credentialUser === 'credentialUser' || this.data.credentialUser === true || this.data.credentialUser === 'true') {
        practitioner['credentialUser'] = true;
        practitioner['ssoUser'] = false;
        practitioner['generalUser'] = false;
        practitioner['practitionerType'] = 'Credential';
      } else if (this.data.credentialUser != 'generalUser' && (this.data.credentialUser === 'ssoUser' || this.data.ssoUser === true || this.data.ssoUser === 'true')) {
        practitioner['ssoUser'] = true;
        practitioner['credentialUser'] = false;
        practitioner['generalUser'] = false;
        practitioner['practitionerType'] = 'SSO';
      } else {
        practitioner['ssoUser'] = false;
        practitioner['credentialUser'] = false;
        practitioner['generalUser'] = true;
        practitioner['practitionerType'] = 'General';
      }

      practitioner['gender'] = this.data.gender;
      practitioner['identifier'] = this.data.identifier;
      practitioner['password'] = this.data.password;
      practitioner['photo'] = this.data.photo;
      practitioner['practitionerRole'] = this.data.practitionerRole;
      practitioner['qualification'] = this.data.qualification;
      practitioner['username'] = this.data.username;
      const telecom = [{system: 'phone', use: 'mobile', value: '', rank: 1, period: {start: '', end: ''}},
        {system: 'phone', use: 'home', value: '', rank: 1, period: {start: '', end: ''}},
        {system: 'email', use: '', value: '', rank: 1, period: {start: '', end: ''}}];
      telecom[0].value = this.data.telecom[0].value;
      telecom[1].value = this.data.telecom[1].value;
      telecom[2].value = this.data.telecom[2].value;
      practitioner['telecom'] = telecom;
      practitioner['authorization'] = this.data.authorization;

      if (has(this.data, 'assignedServer') && !!this.data.assignedServer) {
        practitioner['assignedServers'] = [{
          'resourceType': 'ServerConfiguration',
          'reference': this.data.assignedServer.id
        }];
      }

      this._organizationService.getOrganizationData(this.data.practitionerRole[0].managingOrganization)
        .pipe(
          takeUntil(this._destroyer.asObservable())
        )
        .subscribe(
          (orgDetails) => {
            const body = {'practitioner': practitioner, 'hsdpOrg': orgDetails.externalId};

            this._httpService.post('practitioner', body, true)
              .pipe(
                takeUntil(this._destroyer.asObservable())
              )
              .subscribe(
                (data) => {
                  this._notificationMessageService.success('Practitioner Created Successfully!');
                  console.log('ispm end point ' + data.toString());
                  this._widgetFormInstanceSubmitted = widgetFormInstanceSubmitted;
                  this._changeDetector.markForCheck();
                },
                (error) => {
                  let errMsg: string = error && error.error ? error.error : '';
                  errMsg = 'Failed to Create Practitioner: ' + errMsg;
                  if (error instanceof HttpErrorResponse && has(error, 'error')) {
                    const issues = get(error, 'error.issue', false);

                    if (isArray(issues)) {
                      const errorMessages = [];

                      issues.forEach((issue) => {
                        errorMessages.push(issue?.details?.text);
                      });

                      this._notificationMessageService.error(errorMessages.length ? errorMessages.join('<br />') : 'Failed to Create Practitioner');
                    } else {
                      this._notificationMessageService.error(errMsg);
                    }
                  } else {
                    this._notificationMessageService.error(errMsg);
                  }

                  this._widgetFormInstanceSubmitted = false;
                  this.widgetSubmitStatusValueCallback.apply(this, [false]);
                  this._changeDetector.markForCheck();
                }
              );
          }
        );
    } else if (this.isValid && this.isFormChange && !this.isNewPractitioner) {
      const practitioner = {};
      practitioner['name'] = this.data.name;
      practitioner['active'] = this.data.active;
      practitioner['communication'] = this.data.communication;

      if (this.data.credentialUser === 'credentialUser' || this.data.credentialUser === true || this.data.credentialUser === 'true') {
        practitioner['credentialUser'] = true;
        practitioner['ssoUser'] = false;
        practitioner['generalUser'] = false;
        practitioner['practitionerType'] = 'Credential';
      } else if (this.data.credentialUser === 'ssoUser' || this.data.ssoUser === true || this.data.ssoUser === 'true') {
        practitioner['ssoUser'] = true;
        practitioner['credentialUser'] = false;
        practitioner['generalUser'] = false;
        practitioner['practitionerType'] = 'SSO';
      } else {
        practitioner['ssoUser'] = false;
        practitioner['credentialUser'] = false;
        practitioner['generalUser'] = true;
        practitioner['practitionerType'] = 'General';
      }
      practitioner['gender'] = this.data.gender;
      practitioner['identifier'] = this.data.identifier;
      practitioner['photo'] = this.data.photo;
      practitioner['practitionerRole'] = this.data.practitionerRole;
      practitioner['qualification'] = this.data.qualification;
      const telecom = [{system: 'phone', use: 'mobile', value: '', rank: 1, period: {start: '', end: ''}},
        {system: 'phone', use: 'home', value: '', rank: 1, period: {start: '', end: ''}},
        {system: 'email', use: '', value: '', rank: 1, period: {start: '', end: ''}}];
      telecom[0].value = this.data.telecom[0].value;
      telecom[1].value = this.data.telecom[1].value;
      telecom[2].value = this.data.telecom[2].value;
      practitioner['telecom'] = telecom;
      practitioner['id'] = this.data.id;
      practitioner['authOrgId'] = this.data.organizationId;
      practitioner['organizationId'] = this.data.organizationId;
      practitioner['resourceType'] = this.data.resourceType;
      practitioner['auditId'] = this.data.auditId;
      practitioner['meta'] = this.data.meta;
      practitioner['externalOrganizationId'] = this.data.externalOrganizationId;
      practitioner['authorization'] = this.data.authorization;

      if (has(this.data, 'assignedServer') && !!this.data.assignedServer) {
        practitioner['assignedServers'] = [{
          'resourceType': 'ServerConfiguration',
          'reference': this.data.assignedServer.id
        }];
      }

      this._organizationService.getOrganizationData(this.data.practitionerRole[0].managingOrganization)
        .pipe(
          takeUntil(this._destroyer.asObservable())
        )
        .subscribe(
          (orgDetails) => {
            const body = {'practitioner': practitioner};

            this._httpService.put('practitioner/' + practitioner['id'], body, true)
              .pipe(
                takeUntil(this._destroyer.asObservable())
              )
              .subscribe(
                (data) => {
                  this.data = get(data, 'body');
                  this._notificationMessageService.success('Practitioner Updated Successfully!');
                  this._widgetFormInstanceSubmitted = widgetFormInstanceSubmitted;
                  this.isEditMode = false;
                  this.widgetUpdateValueCallback.apply(this, [this.data]);
                  this._changeDetector.markForCheck();
                },
                (error) => {
                  this._notificationMessageService.error('Failed to Update Practitioner');
                  console.log('ispm end point error => ', error);
                  this._widgetFormInstanceSubmitted = false;
                  this.widgetSubmitStatusValueCallback.apply(this, [false]);
                  this._changeDetector.markForCheck();
                }
              );
          }
        );
    } else {
      if (!this.isFormChange) {
        this._notificationMessageService.warning('Nothing to save, no change was done to the practitioner details');
        this._widgetFormInstanceSubmitted = true;
        this.isEditMode = false;
      } else {
        this._notificationMessageService.error('Some of the required fields are missing or invalid!');
        this._widgetFormInstanceSubmitted = false;
        this.widgetSubmitStatusValueCallback.apply(this, [false]);
      }

      this.widgetUpdateValueCallback.apply(this, [this.data]);
      this._changeDetector.markForCheck();
    }
  }

  public config: any;
  public controls: any;
  public data: any;
  public loading: boolean = true;
  private widgetDataDetails : any;
  public enableEditButton: boolean = false;
  public showEditButton: boolean = false;
  public fromId: any;
  public isEditMode: boolean = false;
  public isFormChange: boolean = false;
  public isNewPractitioner: boolean = false;
  public isValid: boolean = false;
  public listenToDataChange: boolean;
  public originalData: any;
  public restForm: boolean = false;

  public widgetUpdateValueCallback: (value: any) => void ;
  public widgetSubmitStatusValueCallback: (value: boolean) => void;

  private _widgetDisabledStatus: boolean;
  private _widgetFormInstanceSubmitted: boolean;
  private _destroyer: Subject<any> = new Subject();

  constructor(
    private _changeDetector: ChangeDetectorRef,
    private _httpService: HttpInterceptorService,
    private _modalLoaderService: ModalLoaderNgjsService,
    private _notificationMessageService: NotificationMessageService,
    private _organizationService: OrgAdminOrganizationService,
    private _translate: TranslateService
  ) { }

  ngOnDestroy(): void {
    this._destroyer.next();
    this._destroyer.complete();
  }

  updatePractitionerObject(form) {
    if(!isEmpty(this.data?.meta)){
      form['meta'] = this.data?.meta; // changing meta information from latest data to form
    }
    this.listenToDataChange = false;
    if (this.isValid) {
      this.data = form;
      this.isFormChange = true;
    }
  }

  validateChangeEvent(event) {
    this.isValid = event;
  }

  buttonClick(type: string) {
    switch (type) {
      case 'submit':
          this.widgetFormInstanceSubmitted = true;
        break;
      case 'edit':
        this.isEditMode = true;
        this.widgetDisabledStatus = false;
        this._changeDetector.markForCheck();
        break;
      case 'cancel':
        this.cancelClick();
        break;
    }
  }

  private cancelClick() {
    this._modalLoaderService.popupModal(
      'confirm',
      ConfirmModalLoaderPayload.builder()
        .setTitle(this._translate.instant('DISCARD_DATA') || 'Discard Data')
        .setContent(this._translate.instant('ADMIN_UN_SAVED_CHANGES') || 'Canceling will return without saving your changes. Are you sure?')
        .setDeclineButtonText(this._translate.instant('No') || 'No')
        .setApproveButtonText(this._translate.instant('Yes') || 'Yes'),
      'INFO'
    ).subscribe((response) => {
      if (get(response, 'payload') === modalResponse.approve) {
        this.data = this.originalData;
        this.isEditMode = false;
        this.widgetDisabledStatus = true;
        this._changeDetector.markForCheck();
      }
    });
  }

  /**
   * Problems caused because of the empty values passed
   * for practitioner creation under middle name and assigned Server
   * @param someObj
   * @param replaceValue
   */
  private replaceNull(someObj, replaceValue = '') {
    const replacer = (key, value) =>
      String(value) === 'null' || String(value) === 'undefined' ? replaceValue : value;
    return JSON.parse( JSON.stringify(someObj, replacer));
  }
}
